import { PointerEvent, useEffect, useRef, useState } from 'react';

import { VestingGraphIntroPhase } from '../model/vesting-graph.intro';

/** Skip the synthetic pointermove fired when pointer-events return under a still cursor. */
const HOVER_ARM_SUPPRESS_MS = 32;

export const hasVestingGraphHoverMovement = (event: {
  movementX?: null | number;
  movementY?: null | number;
}) => (event.movementX ?? 0) !== 0 || (event.movementY ?? 0) !== 0;

/**
 * Tooltips stay down until the intro has finished and the pointer actually moves over
 * the plot. Closing the grant dropdown leaves the cursor on the chart — that leftover
 * hover must not pop a tooltip, especially while the series is still growing.
 */
export const useVestingGraphHoverGate = ({
  allowHover,
  introPhase,
}: {
  allowHover: boolean;
  introPhase: VestingGraphIntroPhase;
}) => {
  const [isArmed, setIsArmed] = useState(false);
  const suppressUntilMsRef = useRef(0);
  const isPlotInteractive = allowHover && introPhase === 'done';

  useEffect(() => {
    if (!isPlotInteractive) {
      setIsArmed(false);
      return;
    }

    suppressUntilMsRef.current = performance.now() + HOVER_ARM_SUPPRESS_MS;
  }, [isPlotInteractive]);

  return {
    isHoverLive: isPlotInteractive && isArmed,
    isPlotInteractive,
    onPlotPointerLeave: () => setIsArmed(false),
    onPlotPointerMove: (event: PointerEvent<HTMLDivElement>) => {
      if (!isPlotInteractive || isArmed) return;
      if (performance.now() < suppressUntilMsRef.current) return;
      if (!hasVestingGraphHoverMovement(event)) return;
      setIsArmed(true);
    },
  };
};
